import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMapping, updateMapping, listPDFTemplates, listDataSources, getPDFFields, getDataSourceData, Mapping, PDFTemplate, DataSource, PDFField, DataSourceField, MappingDataSource } from '../api/client';
import './ProjectEdit.css';

interface FieldMapping {
  [sourceField: string]: string;
}

interface MappingsBySource {
  [dataSourceId: string]: FieldMapping;
}

const ProjectEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Project data
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [filenamePattern, setFilenamePattern] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedDataSources, setSelectedDataSources] = useState<MappingDataSource[]>([]);
  const [relationships, setRelationships] = useState<Array<{ master_source_id: string; detail_source_id: string; master_field: string; detail_field: string }>>([]);
  const [fieldMappings, setFieldMappings] = useState<MappingsBySource>({});

  // Available options
  const [templates, setTemplates] = useState<PDFTemplate[]>([]);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [pdfFields, setPdfFields] = useState<PDFField[]>([]);
  const [dataSourceFields, setDataSourceFields] = useState<{ [key: string]: DataSourceField[] }>({});

  // UI state
  const [activeTab, setActiveTab] = useState<'info' | 'template' | 'datasources' | 'relationships' | 'mapping'>('info');

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const [mapping, templatesData, dataSourcesData] = await Promise.all([
        getMapping(id),
        listPDFTemplates(),
        listDataSources(),
      ]);

      setName(mapping.name);
      setDescription(mapping.description || '');
      setFilenamePattern(mapping.filename_pattern || '');
      setSelectedTemplateId(mapping.pdf_template_id);
      setSelectedDataSources(mapping.data_sources || []);
      setRelationships(mapping.relationships || []);
      setFieldMappings(mapping.field_mappings || {});

      setTemplates(templatesData);
      setDataSources(dataSourcesData);

      // Load PDF fields
      if (mapping.pdf_template_id) {
        const template = await getPDFFields(mapping.pdf_template_id);
        setPdfFields(template.fields);
      }

      // Load data source fields
      if (mapping.data_sources) {
        const fieldsPromises = mapping.data_sources.map(ds =>
          getDataSourceData(ds.id).then(response => {
            // Use column_order if available, otherwise fall back to Object.keys
            const fieldNames = response.column_order && response.column_order.length > 0
              ? response.column_order
              : Object.keys(response.data || {});
            const fields: DataSourceField[] = fieldNames.map(name => ({ name, type: 'string' }));
            return { id: ds.id, fields };
          })
        );
        const fieldsResults = await Promise.all(fieldsPromises);
        const fieldsMap: { [key: string]: DataSourceField[] } = {};
        fieldsResults.forEach(result => {
          fieldsMap[result.id] = result.fields;
        });
        setDataSourceFields(fieldsMap);
      }
    } catch (err) {
      // Error silently handled
      alert('Fehler beim Laden des Projekts');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = async (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (templateId) {
      try {
        const template = await getPDFFields(templateId);
        setPdfFields(template.fields);
      } catch (err) {
        // Error silently handled
      }
    } else {
      setPdfFields([]);
    }
  };

  const handleAddDataSource = async (dataSourceId: string) => {
    const ds = dataSources.find(d => d.id === dataSourceId);
    if (!ds) return;

    const isPrimary = selectedDataSources.length === 0;
    const newDs: MappingDataSource = {
      id: ds.id,
      type: ds.is_template ? 'master' : 'detail',
      role: isPrimary ? 'primary' : 'secondary',
    };

    setSelectedDataSources([...selectedDataSources, newDs]);

    // Load fields for this data source
    try {
      const response = await getDataSourceData(ds.id);
      // Use column_order if available, otherwise fall back to Object.keys
      const fieldNames = response.column_order && response.column_order.length > 0
        ? response.column_order
        : Object.keys(response.data || {});
      const fields: DataSourceField[] = fieldNames.map(name => ({ name, type: 'string' }));
      setDataSourceFields({ ...dataSourceFields, [ds.id]: fields });
    } catch (err) {
      // Error silently handled
    }
  };

  const handleRemoveDataSource = (dataSourceId: string) => {
    setSelectedDataSources(selectedDataSources.filter(ds => ds.id !== dataSourceId));

    // Remove relationships involving this data source
    setRelationships(relationships.filter(
      rel => rel.master_source_id !== dataSourceId && rel.detail_source_id !== dataSourceId
    ));

    // Remove field mappings for this data source
    const newMappings = { ...fieldMappings };
    delete newMappings[dataSourceId];
    setFieldMappings(newMappings);
  };

  const handleAddRelationship = () => {
    const masterSources = selectedDataSources.filter(ds => ds.type === 'master');
    const detailSources = selectedDataSources.filter(ds => ds.type === 'detail');

    if (masterSources.length === 0 || detailSources.length === 0) {
      alert('Sie benötigen mindestens eine Stammdaten- und eine Detaildaten-Vorlage');
      return;
    }

    setRelationships([
      ...relationships,
      {
        master_source_id: masterSources[0].id,
        detail_source_id: detailSources[0].id,
        master_field: '',
        detail_field: '',
      }
    ]);
  };

  const handleUpdateRelationship = (index: number, field: string, value: string) => {
    const newRelationships = [...relationships];
    newRelationships[index] = { ...newRelationships[index], [field]: value };
    setRelationships(newRelationships);
  };

  const handleRemoveRelationship = (index: number) => {
    setRelationships(relationships.filter((_, i) => i !== index));
  };

  const handleFieldMappingChange = (dataSourceId: string, sourceField: string, targetField: string) => {
    const newMappings = { ...fieldMappings };
    if (!newMappings[dataSourceId]) {
      newMappings[dataSourceId] = {};
    }
    if (targetField) {
      newMappings[dataSourceId][sourceField] = targetField;
    } else {
      delete newMappings[dataSourceId][sourceField];
    }
    setFieldMappings(newMappings);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      alert('Bitte geben Sie einen Projektnamen ein');
      return;
    }
    if (!selectedTemplateId) {
      alert('Bitte wählen Sie ein PDF-Formular aus');
      return;
    }
    if (selectedDataSources.length === 0) {
      alert('Bitte fügen Sie mindestens eine Datenvorlage hinzu');
      return;
    }

    setSaving(true);
    try {
      await updateMapping(id!, {
        name,
        description,
        filename_pattern: filenamePattern,
        pdf_template_id: selectedTemplateId,
        data_sources: selectedDataSources,
        relationships,
        field_mappings: fieldMappings,
      });

      alert('Projekt erfolgreich aktualisiert!');
      navigate('/projects');
    } catch (err: any) {
      // Error silently handled
      alert('Fehler beim Speichern: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const getDataSourceName = (id: string) => {
    return dataSources.find(ds => ds.id === id)?.name || 'Unbekannt';
  };

  if (loading) {
    return (
      <div className="project-edit-container">
        <h1>Lade Projekt...</h1>
      </div>
    );
  }

  return (
    <div className="project-edit-container">
      <div className="edit-header">
        <h1>Projekt bearbeiten</h1>
        <div className="edit-actions">
          <button onClick={() => navigate('/projects')} className="btn-secondary">
            Abbrechen
          </button>
          <button onClick={handleSave} className="btn-primary" disabled={saving}>
            {saving ? 'Speichert...' : 'Speichern'}
          </button>
        </div>
      </div>

      <div className="edit-tabs">
        <button
          className={activeTab === 'info' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('info')}
        >
          1. Projektinfo
        </button>
        <button
          className={activeTab === 'template' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('template')}
        >
          2. PDF-Formular
        </button>
        <button
          className={activeTab === 'datasources' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('datasources')}
        >
          3. Datenvorlagen
        </button>
        <button
          className={activeTab === 'relationships' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('relationships')}
        >
          4. Beziehungen
        </button>
        <button
          className={activeTab === 'mapping' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('mapping')}
        >
          5. Feldzuordnung
        </button>
      </div>

      <div className="edit-content">
        {activeTab === 'info' && (
          <div className="tab-content">
            <h2>Projektinformationen</h2>
            <div className="form-group">
              <label>Projektname *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="form-input"
                placeholder="Mein Projekt"
              />
            </div>
            <div className="form-group">
              <label>Beschreibung</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="form-input"
                rows={4}
                placeholder="Optionale Beschreibung..."
              />
            </div>
            <div className="form-group">
              <label>Dateinamen-Pattern</label>
              <input
                type="text"
                value={filenamePattern}
                onChange={(e) => setFilenamePattern(e.target.value)}
                className="form-input"
                placeholder="z.B. Bescheinigung_{Name}_{date}"
              />
              <small style={{ color: '#666', marginTop: '0.5rem', display: 'block' }}>
                Platzhalter: <code>{`{feldname}`}</code> für Datenfelder, <code>{`{date}`}</code> für Datum (YYYY-MM-DD),
                <code>{`{datetime}`}</code> für Datum+Zeit, <code>{`{timestamp}`}</code> für Unix-Timestamp
              </small>
            </div>
          </div>
        )}

        {activeTab === 'template' && (
          <div className="tab-content">
            <h2>PDF-Formular auswählen</h2>
            <div className="form-group">
              <label>Formular *</label>
              <select
                value={selectedTemplateId}
                onChange={(e) => handleTemplateChange(e.target.value)}
                className="form-input"
              >
                <option value="">-- Bitte wählen --</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            {pdfFields.length > 0 && (
              <div className="field-preview">
                <h3>Verfügbare Felder ({pdfFields.length})</h3>
                <ul className="field-list">
                  {pdfFields.slice(0, 10).map(field => (
                    <li key={field.name}>
                      <strong>{field.name}</strong>
                      <span className="field-type">{field.type}</span>
                    </li>
                  ))}
                  {pdfFields.length > 10 && (
                    <li className="more-fields">... und {pdfFields.length - 10} weitere</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        {activeTab === 'datasources' && (
          <div className="tab-content">
            <h2>Datenvorlagen verwalten</h2>
            <div className="datasources-section">
              <h3>Ausgewählte Datenvorlagen ({selectedDataSources.length})</h3>
              {selectedDataSources.map((ds, idx) => {
                const source = dataSources.find(d => d.id === ds.id);
                return (
                  <div key={ds.id} className="selected-datasource">
                    <div>
                      <strong>{source?.name || 'Unbekannt'}</strong>
                      <span className={`badge badge-${ds.type}`}>{ds.type}</span>
                      {ds.role === 'primary' && <span className="badge badge-primary">Primär</span>}
                    </div>
                    <div>
                      <select
                        value={ds.type}
                        onChange={(e) => {
                          const newDs = [...selectedDataSources];
                          newDs[idx].type = e.target.value as 'master' | 'detail';
                          setSelectedDataSources(newDs);
                        }}
                        className="form-input-small"
                      >
                        <option value="master">Stammdaten</option>
                        <option value="detail">Detaildaten</option>
                      </select>
                      <button
                        onClick={() => handleRemoveDataSource(ds.id)}
                        className="btn-small btn-danger"
                      >
                        Entfernen
                      </button>
                    </div>
                  </div>
                );
              })}

              <div className="form-group">
                <label>Neue Datenvorlage hinzufügen</label>
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAddDataSource(e.target.value);
                      e.target.value = '';
                    }
                  }}
                  className="form-input"
                >
                  <option value="">-- Datenvorlage auswählen --</option>
                  {dataSources
                    .filter(ds => !selectedDataSources.find(s => s.id === ds.id))
                    .map(ds => (
                      <option key={ds.id} value={ds.id}>
                        {ds.name} ({ds.type})
                      </option>
                    ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'relationships' && (
          <div className="tab-content">
            <h2>Beziehungen zwischen Datenvorlagen</h2>
            {relationships.map((rel, idx) => (
              <div key={idx} className="relationship-item">
                <div className="relationship-fields">
                  <div className="form-group">
                    <label>Stammdaten-Vorlage</label>
                    <select
                      value={rel.master_source_id}
                      onChange={(e) => handleUpdateRelationship(idx, 'master_source_id', e.target.value)}
                      className="form-input"
                    >
                      {selectedDataSources.filter(ds => ds.type === 'master').map(ds => (
                        <option key={ds.id} value={ds.id}>{getDataSourceName(ds.id)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Stammdaten-Feld</label>
                    <select
                      value={rel.master_field}
                      onChange={(e) => handleUpdateRelationship(idx, 'master_field', e.target.value)}
                      className="form-input"
                    >
                      <option value="">-- Feld wählen --</option>
                      {dataSourceFields[rel.master_source_id]?.map(field => (
                        <option key={field.name} value={field.name}>{field.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Detaildaten-Vorlage</label>
                    <select
                      value={rel.detail_source_id}
                      onChange={(e) => handleUpdateRelationship(idx, 'detail_source_id', e.target.value)}
                      className="form-input"
                    >
                      {selectedDataSources.filter(ds => ds.type === 'detail').map(ds => (
                        <option key={ds.id} value={ds.id}>{getDataSourceName(ds.id)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Detaildaten-Feld</label>
                    <select
                      value={rel.detail_field}
                      onChange={(e) => handleUpdateRelationship(idx, 'detail_field', e.target.value)}
                      className="form-input"
                    >
                      <option value="">-- Feld wählen --</option>
                      {dataSourceFields[rel.detail_source_id]?.map(field => (
                        <option key={field.name} value={field.name}>{field.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveRelationship(idx)}
                  className="btn-small btn-danger"
                >
                  Löschen
                </button>
              </div>
            ))}
            <button onClick={handleAddRelationship} className="btn-secondary">
              + Beziehung hinzufügen
            </button>
          </div>
        )}

        {activeTab === 'mapping' && (
          <div className="tab-content">
            <h2>Feldzuordnung konfigurieren</h2>
            {selectedDataSources.map(ds => {
              const fields = dataSourceFields[ds.id] || [];
              const mappings = fieldMappings[ds.id] || {};

              return (
                <div key={ds.id} className="mapping-section">
                  <h3>{getDataSourceName(ds.id)}</h3>
                  <div className="mapping-table">
                    <div className="mapping-header">
                      <div>Quellfeld</div>
                      <div>→</div>
                      <div>PDF-Feld</div>
                    </div>
                    {fields.map(field => (
                      <div key={field.name} className="mapping-row">
                        <div className="source-field">{field.name}</div>
                        <div>→</div>
                        <div>
                          <select
                            value={mappings[field.name] || ''}
                            onChange={(e) => handleFieldMappingChange(ds.id, field.name, e.target.value)}
                            className="form-input-small"
                          >
                            <option value="">-- Nicht gemappt --</option>
                            {pdfFields.map(pdfField => (
                              <option key={pdfField.name} value={pdfField.name}>
                                {pdfField.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectEdit;
